const LINKED_ORDER_STATUS_TYPES = ["custom_order", "repair_order", "reserve"];

export const isCannotAddToCart = (product) =>
  product?.cannotaddtocart === true ||
  product?.cannotaddtocart === 1 ||
  product?.cannotaddtocart === "1";

const resolveLinkedOrderType = (product) => {
  if (LINKED_ORDER_STATUS_TYPES.includes(product?.status_type)) {
    return product.status_type;
  }
  return product?.order_type || product?.status_type || "custom_order";
};

export const getLinkedOrderPosscreenState = (product) => {
  if (!product?.linked_order_id) return null;

  const orderType = resolveLinkedOrderType(product);
  const orderEntry = {
    id: product.linked_order_id,
    order_type: orderType,
    linked_cart_id: product?.linked_cart_id || product?.linkedCartId || "",
    stock_id: product?.stock_id || "",
    po_product_id: product?.po_product_id || "",
  };

  if (orderType === "custom_order") {
    orderEntry.by_custom_order = product?.by_custom_order ?? true;
  } else if (orderType === "repair_order") {
    orderEntry.by_repair_order = product?.by_repair_order ?? true;
  } else if (orderType === "reserve") {
    orderEntry.reserve_sell = true;
  }

  if (isCannotAddToCart(product)) {
    orderEntry.collect_at_holding_location = true;
  }

  return {
    state1: {
      order_id: [orderEntry],
      order_type: orderType,
      ...(orderType === "custom_order" && {
        by_custom_order: orderEntry.by_custom_order ?? true,
      }),
      ...(orderType === "repair_order" && {
        by_repair_order: orderEntry.by_repair_order ?? true,
      }),
    },
  };
};

export const buildLinkedOrderEntry = (product) =>
  getLinkedOrderPosscreenState(product)?.state1?.order_id?.[0] || null;

export const sameId = (left, right) =>
  Boolean(left) && Boolean(right) && String(left) === String(right);

export const isSameLinkedOrderEntry = (left, right) =>
  sameId(left?.po_product_id, right?.po_product_id);

export const isLinkedSearchItem = (result) =>
  Boolean(result?.linked_order_id || result?.__searchSource === "linked");

const isLinkedCartRow = (item) =>
  Boolean(
    item?.linked_order_id ||
    item?.linked_cart_id ||
    LINKED_ORDER_STATUS_TYPES.includes(item?.type) ||
    LINKED_ORDER_STATUS_TYPES.includes(item?.order_type) ||
    LINKED_ORDER_STATUS_TYPES.includes(item?.status_type)
  );

const cartPoProductIds = (cart = [], linkedOrders = []) => {
  const ids = new Set();
  cart.forEach((item) => {
    if (isLinkedCartRow(item) && item?.po_product_id) {
      ids.add(String(item.po_product_id));
    }
  });
  linkedOrders.forEach((entry) => {
    if (entry?.po_product_id) ids.add(String(entry.po_product_id));
  });
  return ids;
};

const inStockCartIds = (cart = []) => {
  const ids = new Set();
  cart.forEach((item) => {
    if (isLinkedCartRow(item)) {
      return;
    }
    if (item?._id) ids.add(String(item._id));
    if (item?.product_id) ids.add(String(item.product_id));
  });
  return ids;
};

const cartLinkedStockIds = (cart = [], linkedOrders = []) => {
  const ids = new Set();
  cart.forEach((item) => {
    if (item?.stock_id) ids.add(String(item.stock_id));
  });
  linkedOrders.forEach((entry) => {
    if (entry?.stock_id) ids.add(String(entry.stock_id));
  });
  return ids;
};

export const filterInStockSearchItems = (items = [], cart = []) => {
  const inCart = inStockCartIds(cart);
  return items.filter((item) => !item?._id || !inCart.has(String(item._id)));
};

export const filterLinkedSearchItems = (
  items = [],
  cart = [],
  linkedOrders = []
) => {
  const inCartPoIds = cartPoProductIds(cart, linkedOrders);
  const inCartStockIds = cartLinkedStockIds(cart, linkedOrders);
  return items.filter((item) => {
    if (
      item?.po_product_id &&
      inCartPoIds.has(String(item.po_product_id))
    ) {
      return false;
    }
    if (item?.stock_id && inCartStockIds.has(String(item.stock_id))) {
      return false;
    }
    return true;
  });
};

export const isSearchItemInCart = (result, cart = [], linkedOrders = []) => {
  if (!result) {
    return false;
  }
  if (isLinkedSearchItem(result)) {
    const poId = String(result.po_product_id || "");
    const stockId = String(result.stock_id || "");
    return (
      (poId && cartPoProductIds(cart, linkedOrders).has(poId)) ||
      (stockId && cartLinkedStockIds(cart, linkedOrders).has(stockId))
    );
  }
  return inStockCartIds(cart).has(String(result._id || ""));
};

export const matchesLinkedOrderEntry = (entry, cartItem) => {
  if (!entry || !cartItem) {
    return false;
  }
  if (
    sameId(entry.linked_cart_id, cartItem.linked_cart_id) ||
    sameId(entry.linked_cart_id, cartItem.custom_cart_id)
  ) {
    return true;
  }
  if (!isLinkedCartRow(cartItem)) {
    return false;
  }
  if (sameId(entry.po_product_id, cartItem.po_product_id)) {
    return true;
  }
  if (sameId(entry.stock_id, cartItem.stock_id)) {
    return true;
  }
  if (entry.po_product_id || entry.linked_cart_id || entry.stock_id) {
    return false;
  }
  return (
    sameId(entry.id, cartItem.linked_order_id) ||
    sameId(entry.id, cartItem.order_id) ||
    sameId(entry.id, cartItem.custom_order_id)
  );
};

const isOrderLevelLinkedEntry = (entry) =>
  Boolean(entry?.id) &&
  !entry?.po_product_id &&
  !entry?.linked_cart_id &&
  !entry?.stock_id;

const cartBelongsToOrderEntry = (entry, item) =>
  sameId(entry?.id, item?.linked_order_id) ||
  sameId(entry?.id, item?.order_id) ||
  sameId(entry?.id, item?.custom_order_id);

export const hydrateLinkedOrdersFromCart = (linkedOrders = [], cart = []) => {
  if (!Array.isArray(linkedOrders) || !linkedOrders.length) {
    return Array.isArray(linkedOrders) ? linkedOrders : [];
  }
  if (!Array.isArray(cart) || !cart.length) {
    return linkedOrders;
  }

  const expanded = [];
  linkedOrders.forEach((entry) => {
    if (isOrderLevelLinkedEntry(entry)) {
      let lines = cart.filter((item) => cartBelongsToOrderEntry(entry, item));
      if (!lines.length) {
        const onlyThisOrder =
          linkedOrders.filter(isOrderLevelLinkedEntry).length === 1;
        if (onlyThisOrder) {
          lines = cart.filter(isLinkedCartRow);
        }
      }
      if (!lines.length) {
        expanded.push(entry);
        return;
      }
      lines.forEach((item) => {
        expanded.push({
          ...entry,
          linked_cart_id: item.linked_cart_id || item.custom_cart_id || "",
          po_product_id: item.po_product_id || "",
          stock_id: item.stock_id || "",
          ...(isCannotAddToCart(item) || entry.collect_at_holding_location
            ? { collect_at_holding_location: true }
            : {}),
        });
      });
      return;
    }

    const cartItem = cart.find((item) => matchesLinkedOrderEntry(entry, item));
    expanded.push({
      ...entry,
      po_product_id: entry.po_product_id || cartItem?.po_product_id || "",
      linked_cart_id:
        entry.linked_cart_id ||
        cartItem?.linked_cart_id ||
        cartItem?.custom_cart_id ||
        "",
      stock_id: entry.stock_id || cartItem?.stock_id || "",
      ...(isCannotAddToCart(cartItem) || entry.collect_at_holding_location
        ? { collect_at_holding_location: true }
        : {}),
    });
  });
  return expanded;
};

export const filterCartToRequestedLinkedLines = (cart, linkedOrders) => {
  if (!Array.isArray(cart)) {
    return [];
  }
  const orders = Array.isArray(linkedOrders) ? linkedOrders : [];
  const allowedPoProductIds = new Set(
    orders.map((entry) => String(entry?.po_product_id || "")).filter(Boolean)
  );
  if (!allowedPoProductIds.size) {
    return cart;
  }
  return cart.filter((item) => {
    if (!isLinkedCartRow(item)) {
      return true;
    }
    if (item?.po_product_id) {
      return allowedPoProductIds.has(String(item.po_product_id));
    }
    return orders.some((entry) => matchesLinkedOrderEntry(entry, item));
  });
};

export const canSelectInventoryProduct = (result, customer) => {
  if (
    result?.status_type === "transit" ||
    result?.status_type === "warehouse"
  ) {
    return false;
  }
  if (result?.status_type === "custom_order") {
    return customer?.id === result?.pos?.customer_id;
  }
  if (result?.status_type === "repair_order") {
    return customer?.id === result?.customer_id;
  }
  if (result?.status_type === "reserve") {
    return Boolean(
      result?.linked_order_id && customer?.id === result?.reserve_customer_id
    );
  }
  if (result?.pos?.customer_id || result?.customer_id) return false;
  return true;
};

export const canShowInventoryAddToCart = (result, customer, addparmission) => {
  if (!addparmission) return false;
  return canSelectInventoryProduct(result, customer);
};
